import { useState, useCallback } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { ScoutIntent } from "@/common/utils/types";
import { uploadDocumentTemplatePdf } from "@/common/lib/supabaseClient";
import { createCustomDocumentTypeFromPdf } from "@/common/lib/api";
import { getSafeErrorInfo } from "@/common/utils/helpers";
import { useScoutChatHistories } from "@/state/hooks/useScoutChat";

interface ScoutDropzoneProps {
    onDrop?: () => Promise<void>;
}

/**
 * Hook to handle Scout dropzone functionality including PDF drops and action intents.
 * Manages dropped PDF file state, dropzone props, and processes action intents like creating document templates.
 * @returns Object containing dropzone props, handlers, and state for Scout actions
 */
export const useScoutDropzone = ({ onDrop = async () => {} }: ScoutDropzoneProps) => {
    const {
        query: { data: scoutChatHistories },
        addMessageMutation,
        updateMessagesMutation,
    } = useScoutChatHistories();
    const activeChatHistoryId = scoutChatHistories?.[0]?.id;

    const [droppedPdfFile, setDroppedPdfFile] = useState<File | null>(null);

    /**
     * Clears the dropped PDF file and cancels any pending create-document-template-from-pdf actions.
     */
    const cancelDroppedPdf = useCallback(() => {
        setDroppedPdfFile(null);
        updateMessagesMutation.mutate({
            chatHistoryId: activeChatHistoryId,
            match: { actionIntent: "create-document-template-from-pdf", actionStatus: "pending" },
            update: { actionStatus: "cancelled" },
        });
    }, [activeChatHistoryId, updateMessagesMutation]);

    /**
     * Handles files dropped into the Scout chat.
     * @param acceptedFiles - Array of files dropped by the user
     */
    const handleDrop = useCallback(
        async (acceptedFiles: File[]) => {
            await onDrop();
            if (acceptedFiles.length === 0 || !activeChatHistoryId) return;

            const pdfFile = acceptedFiles[0]; // Take the first PDF
            setDroppedPdfFile(pdfFile);

            // Add message indicating the user dropped a PDF
            addMessageMutation.mutate({
                chatHistoryId: activeChatHistoryId,
                message: {
                    text: `Added file \`${pdfFile.name}\``,
                    isUser: true,
                    timestamp: new Date(),
                    actionIntent: "drop-file",
                    actionStatus: null,
                },
            });
            await new Promise((resolve) => setTimeout(resolve, 300));
            // Add a message asking about creating a document template
            addMessageMutation.mutate({
                chatHistoryId: activeChatHistoryId,
                message: {
                    text: `Would you like to create a custom document template from \`${pdfFile.name}\`?`,
                    isUser: false,
                    timestamp: new Date(),
                    actionIntent: "create-document-template-from-pdf",
                    actionStatus: "pending",
                },
            });
        },
        [activeChatHistoryId, addMessageMutation, onDrop],
    );

    const handleDropRejected = useCallback(
        async (rejectedFiles: FileRejection[]) => {
            if (rejectedFiles.length === 0 || !activeChatHistoryId) return;
            const rejectedFile = rejectedFiles[0].file;
            // Add messages indicating the user dropped a PDF that was rejected
            addMessageMutation.mutate({
                chatHistoryId: activeChatHistoryId,
                message: {
                    text: `Added file \`${rejectedFile.name}\``,
                    isUser: true,
                    timestamp: new Date(),
                    actionIntent: "drop-file",
                    actionStatus: null,
                },
            });
            await new Promise((resolve) => setTimeout(resolve, 300));
            addMessageMutation.mutate({
                chatHistoryId: activeChatHistoryId,
                message: {
                    text: `That file doesn't look like a PDF. If you give me a PDF, I can create a custom document template from it.`,
                    isUser: false,
                    timestamp: new Date(),
                    actionStatus: null,
                },
            });
        },
        [activeChatHistoryId, addMessageMutation],
    );

    const {
        getRootProps: getDropzoneRootProps,
        getInputProps: getDropzoneInputProps,
        isDragActive,
    } = useDropzone({
        disabled: !activeChatHistoryId || !!droppedPdfFile,
        onDrop: handleDrop,
        onDropRejected: handleDropRejected,
        accept: {
            "application/pdf": [".pdf"],
        },
        multiple: false,
        noClick: true,
    });

    /**
     * Handles the reject action for any dropzone action intents by clearing the dropped PDF.
     * @param actionIntent - The action intent to handle
     */
    const handleRejectDropzoneAction = useCallback(
        (actionIntent: ScoutIntent | undefined) => {
            switch (actionIntent) {
                case "create-document-template-from-pdf":
                    setDroppedPdfFile(null);
                    updateMessagesMutation.mutate({
                        chatHistoryId: activeChatHistoryId,
                        match: { actionIntent: "create-document-template-from-pdf", actionStatus: "pending" },
                        update: { actionStatus: "rejected" },
                    });
                    break;
                default:
                    break;
            }
        },
        [activeChatHistoryId, updateMessagesMutation],
    );

    /**
     * Handles the accept action for any dropzone action intents
     * @param actionIntent - The action intent to handle
     */
    const handleAcceptDropzoneAction = useCallback(
        async (actionIntent: ScoutIntent | undefined) => {
            switch (actionIntent) {
                case "create-document-template-from-pdf":
                    if (!droppedPdfFile) {
                        break;
                    }
                    try {
                        addMessageMutation.mutate({
                            chatHistoryId: activeChatHistoryId,
                            message: {
                                text: `Okay! I'm uploading and analyzing \`${droppedPdfFile.name}\` now. This may take a few minutes...`,
                                isUser: false,
                                timestamp: new Date(),
                                actionStatus: "working",
                            },
                        });

                        const { error, filename } = await uploadDocumentTemplatePdf(
                            droppedPdfFile,
                            droppedPdfFile.name,
                        );
                        if (error || !filename) {
                            throw new Error("Upload failed");
                        }
                        const documentType = await createCustomDocumentTypeFromPdf(filename);

                        updateMessagesMutation.mutate({
                            chatHistoryId: activeChatHistoryId,
                            match: { actionStatus: "working" },
                            update: { actionStatus: "completed" },
                        });
                        addMessageMutation.mutate({
                            chatHistoryId: activeChatHistoryId,
                            message: {
                                text: `Your [new document template](/document-builder?SOAP=${documentType.id}&template=json) is ready! Please review it and make adjustments before enabling it.`,
                                isUser: false,
                                timestamp: new Date(),
                                actionStatus: null,
                            },
                        });
                    } catch (error) {
                        const { message: errorMessage } = getSafeErrorInfo(error);
                        updateMessagesMutation.mutate({
                            chatHistoryId: activeChatHistoryId,
                            match: { actionStatus: "working" },
                            update: { actionStatus: "failed" },
                        });
                        addMessageMutation.mutate({
                            chatHistoryId: activeChatHistoryId,
                            message: {
                                text:
                                    errorMessage === "Upload failed"
                                        ? "Sorry, I couldn't upload your PDF. Please try again."
                                        : "Sorry, I ran into an error processing your PDF. Please try again.",
                                isUser: false,
                                timestamp: new Date(),
                                actionStatus: null,
                            },
                        });
                    } finally {
                        // Clear the dropped PDF when finished
                        setDroppedPdfFile(null);
                    }
                    break;
                default:
                    break;
            }
        },
        [activeChatHistoryId, addMessageMutation, updateMessagesMutation, droppedPdfFile, setDroppedPdfFile],
    );

    return {
        droppedPdfFile,
        cancelDroppedPdf,
        handleAcceptDropzoneAction,
        handleRejectDropzoneAction,
        getDropzoneRootProps,
        getDropzoneInputProps,
        isDragActive,
    };
};
